import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import WireSection from '../components/WireSection.jsx';
import { getArticleImage } from '../data/articleImages.js';
import Seo from '../components/Seo.jsx';
import { STATIC_SEO } from '../data/seo.js';
import { truncatePlain } from '../utils/plainText.js';

// ─────────────────────────────────────────────────────────────────────────
// HOMEPAGE — editorial cover-style
// Window Washing Guide — Issue 042
// ─────────────────────────────────────────────────────────────────────────

// Pull the 3 most recent blog posts at build time for the homepage strip.
const blogModules = import.meta.glob('../blog/enriched/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

function parseBlogFrontmatter(raw) {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---/);
  if (!match) return {};
  const yamlText = match[1];
  const fm = {};
  let currentKey = null;
  for (const line of yamlText.split('\n')) {
    if (line.trim() === '' || line.trim().startsWith('#')) continue;
    const topMatch = line.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (topMatch && !line.startsWith(' ')) {
      currentKey = topMatch[1];
      const value = topMatch[2].trim();
      if (value === '') {
        fm[currentKey] = null;
      } else if (value.startsWith('"') && value.endsWith('"')) {
        fm[currentKey] = value.slice(1, -1);
      } else {
        fm[currentKey] = value;
      }
    } else {
      const subMatch = line.match(/^  ([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
      if (subMatch) {
        const subKey = subMatch[1];
        const subValue = subMatch[2].trim();
        if (!fm[currentKey] || typeof fm[currentKey] !== 'object') {
          fm[currentKey] = {};
        }
        fm[currentKey][subKey] = subValue.startsWith('"') && subValue.endsWith('"')
          ? subValue.slice(1, -1) : subValue;
      }
    }
  }
  return fm;
}

const RECENT_BLOG_POSTS = (() => {
  const list = [];
  for (const [, raw] of Object.entries(blogModules)) {
    const fm = parseBlogFrontmatter(raw);
    if (fm.slug) list.push(fm);
  }
  return list
    .sort((a, b) => (b.publishedDate || '').localeCompare(a.publishedDate || ''))
    .slice(0, 3);
})();

const BLOG_CATEGORY_LABELS = {
  seasonal: 'Seasonal',
  industry: 'Industry',
  'how-to': 'How-to',
  opinion: 'Opinion',
  product: 'Product',
};

const PILLARS = [
  { num: '01', kicker: 'Foundation', name: 'Encyclopedia', italic: 'Encyclopedia',
    desc: 'Every surface, every tool, every problem — documented. The workhorse pages.',
    count: '5 ENTRIES', path: '/encyclopedia' },
  { num: '02', kicker: 'Symptom-first', name: 'Diagnostician', italic: 'Diagnostician',
    desc: "Start with what's wrong. Find the cause. Fix it once. Stop relearning.",
    count: '11 ENTRIES', path: '/diagnostician' },
  { num: '03', kicker: 'Long-form', name: 'Field Notes', italic: 'Notes',
    nameMain: 'Field',
    desc: 'Reported essays. Original photography. The craft as worth taking seriously.',
    count: '2 ENTRIES', path: '/field-notes' },
  { num: '04', kicker: 'Trade', name: 'Pro Track', italic: 'Pro Track',
    desc: 'Pricing, equipment, route theory. Insider knowledge for the working cleaner.',
    count: '2 ENTRIES', path: '/pro-track' },
  { num: '05', kicker: 'Reference', name: 'Glossary', italic: 'Glossary',
    desc: 'Every term in the trade, defined by the people who use them on routes. Cross-referenced.',
    count: '190 TERMS', path: '/glossary' },
];

const TOOLS = [
  { num: '01', tag: '◆ MOST USED', name: 'Streak Diagnostic', italic: 'Diagnostic',
    desc: "Two questions, one verdict. Identify what's actually wrong with your window — and find the article that walks you through the fix.",
    icon: '◷', path: '/tools/streak-diagnostic', signature: true },
  { num: '02', tag: '◆ GEO-AWARE', name: 'Hard Water Severity Scorer', italic: 'Severity Scorer',
    desc: "Enter your zip code. We pull regional water hardness data and tell you exactly what you're dealing with.",
    icon: '◇', path: '/tools/hard-water-scorer' },
  { num: '03', tag: '◆ PRINT-READY', name: 'The Solution Calculator', italic: 'Calculator',
    desc: 'Exact ratios for any volume of any cleaning recipe. Save your formulas. Print the labels.',
    icon: '▦', path: '/tools/solution-calculator' },
  { num: '04', tag: '◆ ANIMATED', name: 'The Technique Library', italic: 'Library',
    desc: 'Eight chapters of animated technique reference — squeegee, detail, razor, application, and more. The canonical reference.',
    icon: '✛', path: '/tools/technique-library' },
  { num: '05', tag: '◆ REGIONAL DATA', name: 'Cost Estimator', italic: 'Estimator',
    desc: 'Square footage, story, accessibility, region. DIY vs. pro pricing in one glance.',
    icon: '⊕', path: '/tools/cost-estimator' },
  { num: '06', tag: '◆ DAMAGE-PREVENTION', name: 'Tint & Coating Identifier', italic: 'Identifier',
    desc: 'Visual quiz to identify your glass before you ruin it. Tempered, low-E, laminated, tinted.',
    icon: '☐', path: '/tools/tint-identifier' },
];

const ARTICLES = [
  { num: '001', title: 'Why your windows look worse after you clean them', italic: 'worse', slug: 'why-windows-look-worse' },
  { num: '003', title: 'The vinegar question: when it works, when it ruins your glass', italic: 'when it works, when it ruins your glass', slug: 'vinegar-question-when-it-works' },
  { num: '006', title: "Foggy windows that won't clear: the failed-seal diagnosis", italic: 'failed-seal diagnosis', slug: 'foggy-windows-failed-seal' },
  { num: '008', title: 'Scratches after cleaning: fabricating debris and the dirty secret of tempered glass', italic: 'tempered glass', slug: 'scratches-after-cleaning' },
  { num: '012', title: 'Hard water etching vs. surface deposits: the test that tells you which you have', italic: 'the test that tells you which', slug: 'hard-water-etching-vs-deposits' },
  { num: '014', title: "The 'permanent fog' myth: when your hazy window is fixable, and when it really isn't", italic: 'permanent fog', slug: 'permanent-fog-myth' },
];

export default function Homepage() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const T = {
    bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
    inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
    sienna: '#B5532A', sage: '#9CA89A',
  };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  }).toUpperCase();

  function handleSubscribe(e) {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
    }
  }

  return (
    <div style={{
      background: T.bg, color: T.ink, fontFamily: fontBody,
      minHeight: '100vh',
    }}>
      <Seo
        title={STATIC_SEO['/'].metaTitle}
        description={STATIC_SEO['/'].metaDescription}
        path="/"
        ogType="website"
      />
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .reveal { animation: fadeUp 0.7s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .reveal-4 { animation-delay: 0.35s; }

        .pillar-tile { transition: background 0.2s, transform 0.2s; cursor: pointer; }
        .pillar-tile:hover { background: ${T.surface}; }
        .pillar-tile:hover .arrow { transform: translateX(4px); }
        .arrow { display: inline-block; transition: transform 0.2s; }

        .tool-tile { transition: background 0.2s, transform 0.2s, border-color 0.2s; cursor: pointer; }
        .tool-tile:hover { background: ${T.surface}; border-color: ${T.sienna}; }

        .article-row { transition: background 0.2s, padding-left 0.2s; cursor: pointer; }
        .article-row:hover { background: ${T.surface}; padding-left: 24px; }

        .hp-row {
          display: grid;
          grid-template-columns: 56px 72px 1fr 32px;
          gap: 20px;
          align-items: center;
          padding: 18px 0;
        }
        @media (max-width: 600px) {
          .hp-row {
            grid-template-columns: 48px 1fr 24px;
            gap: 14px;
          }
          .hp-row .hp-num { display: none; }
        }

        .ghost-link:hover { color: ${T.sienna}; }

        @media (max-width: 820px) {
          .pillars-grid { grid-template-columns: 1fr !important; }
          .tools-grid { grid-template-columns: 1fr !important; }
          .blog-strip { grid-template-columns: 1fr !important; }
          .blog-strip-card { border-right: none !important; }
          .hero-h1 { font-size: 56px !important; line-height: 1.05 !important; }
          .container { padding: 24px 20px 60px !important; }
          /* s139: full-bleed feature must match the 20px phone gutter, not the 28px desktop one */
          .hp-feature { margin-left: -20px !important; margin-right: -20px !important; padding: 48px 20px !important; }
        }
      `}</style>

      {/* Cover masthead */}
      <header className="container" style={{
        maxWidth: 1080, margin: '0 auto', padding: '32px 28px 0',
      }}>
        <div style={{
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`,
        }}>
          <div style={{ fontFamily: fontDisplay, fontSize: 32, letterSpacing: '-0.015em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          paddingTop: 16, paddingBottom: 24,
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.inkFaint,
        }}>
          <span>VOL. 01 / ISSUE 042</span>
          <span>{today}</span>
        </div>
      </header>

      <div className="container" style={{
        maxWidth: 1080, margin: '0 auto', padding: '0 28px 80px',
      }}>
        {/* Tagline rail */}
        <div className="reveal reveal-1" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 0', borderTop: `1px solid ${T.ink}`, borderBottom: `1px solid ${T.ink}`,
          marginBottom: 80,
          fontFamily: fontDisplay, fontStyle: 'italic',
          color: T.inkFaint, fontSize: 16,
        }}>
          <span>Streak-free is a chemistry problem</span>
          <span style={{ color: T.sienna, fontStyle: 'normal' }}>◆</span>
          <span>Vinegar is overrated</span>
        </div>

        {/* The Reference / Pillars section */}
        <section className="reveal reveal-2" style={{ marginBottom: 100 }}>
          <div style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            marginBottom: 16,
          }}>
            § THE REFERENCE
          </div>
          {/* s79 (audit D9): the homepage's ONE <h1>. It was an <h2>; the
              only <h1> on the page was the noscript brand block, which is
              now a <p>. Copy names the head term the page targets. */}
          <h1 className="hero-h1" style={{
            fontFamily: fontDisplay, fontSize: 64, fontWeight: 400,
            lineHeight: 1.06, letterSpacing: '-0.025em',
            margin: '0 0 12px', maxWidth: 880,
          }}>
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              How to clean windows without streaks
            </em>
            , and everything else we know about glass.
          </h1>

          <div className="pillars-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 0, marginTop: 56,
            borderTop: `1px solid ${T.rule}`,
          }}>
            {PILLARS.map((p, i) => (
              <Link
                key={p.num} to={p.path}
                className="pillar-tile"
                style={{
                  display: 'block', textDecoration: 'none', color: T.ink,
                  padding: '32px 24px 32px 0',
                  paddingLeft: i % 2 === 1 ? 24 : 0,
                  borderRight: i % 2 === 0 ? `1px solid ${T.rule}` : 'none',
                  borderBottom: `1px solid ${T.rule}`,
                  minHeight: 180,
                }}
              >
                <div style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sage,
                  marginBottom: 14,
                }}>
                  {p.num} / {p.kicker}
                </div>
                <h3 style={{
                  fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                  lineHeight: 1.1, letterSpacing: '-0.02em',
                  margin: '0 0 10px',
                }}>
                  {p.nameMain ? <>{p.nameMain} <em style={{ fontStyle: 'italic', fontWeight: 400 }}>{p.italic}</em></> : <>The <em style={{ fontStyle: 'italic', fontWeight: 400 }}>{p.italic}</em></>}
                </h3>
                <p style={{
                  fontFamily: fontDisplay, fontSize: 15, fontWeight: 300,
                  lineHeight: 1.55, color: T.inkSoft,
                  margin: '0 0 16px', maxWidth: 360,
                }}>
                  {p.desc}
                </p>
                <div style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                }}>
                  {p.count} <span className="arrow">→</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Field Note feature */}
        <section className="reveal reveal-3 hp-feature" style={{
          background: T.ink, color: T.bg,
          padding: '64px 48px', marginBottom: 100,
          marginLeft: -28, marginRight: -28,
        }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            {/* Feature photo — Burj Khalifa rope-access cleaners */}
            {(() => {
              const burj = getArticleImage('burj-khalifa-cleaners');
              return burj ? (
                <div style={{
                  width: '100%', aspectRatio: '16 / 9',
                  overflow: 'hidden', marginBottom: 32,
                  border: '1px solid #2E3236',
                }}>
                  <img
                    src={burj.hero}
                    alt={burj.alt}
                    width={1200}
                    height={675}
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover', display: 'block',
                    }}
                  />
                </div>
              ) : null;
            })()}
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: '#D67148',
              marginBottom: 20,
            }}>
              FIELD NOTE 042 · LONG READ · 22 MIN
            </div>
            <h2 style={{
              fontFamily: fontDisplay, fontSize: 48, fontWeight: 400,
              lineHeight: 1.1, letterSpacing: '-0.02em',
              margin: '0 0 24px',
            }}>
              The men who clean the <em style={{ fontStyle: 'italic', color: '#B8B2A4', fontWeight: 400 }}>tallest building</em> in the world.
            </h2>
            <p style={{
              fontFamily: fontDisplay, fontSize: 19, fontWeight: 300,
              lineHeight: 1.6, color: '#B8B2A4',
              margin: '0 0 16px',
            }}>
              At 828 meters, the Burj Khalifa is a vertical city of glass — 24,348 individual panes that, together, would cover seventeen American football fields. A team of thirty-six rope-access technicians cleans them on a rotation that takes between three and four months to complete. By the time they finish, it is time to start again.
            </p>
            <p style={{
              fontFamily: fontDisplay, fontSize: 19, fontWeight: 300,
              lineHeight: 1.6, color: '#B8B2A4',
              margin: '0 0 32px',
            }}>
              What follows is what they taught us about water, wind, and the strange psychology of working at the edge of nothing.
            </p>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em',
              paddingTop: 24, borderTop: `1px solid #2E3236`,
            }}>
              <span style={{ color: '#6B6960' }}>BY DANIEL REYES</span>
              <Link to="/articles/burj-khalifa-cleaners" style={{
                color: '#D67148', textDecoration: 'none',
              }}>
                READ THE FULL PIECE →
              </Link>
            </div>
          </div>
        </section>

        {/* Tools section */}
        <section className="reveal reveal-3" style={{ marginBottom: 100 }}>
          <div style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            marginBottom: 16,
          }}>
            § THE TOOLS
          </div>
          <h2 className="hero-h1" style={{
            fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
            lineHeight: 1.06, letterSpacing: '-0.025em',
            margin: '0 0 12px', maxWidth: 800,
          }}>
            Built for the work, not the page. <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>Diagnostics, calculators, and references</em> that solve actual problems in seconds.
          </h2>

          <div className="tools-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 0, marginTop: 56,
            borderTop: `1px solid ${T.rule}`,
          }}>
            {TOOLS.map((tool, i) => (
              <Link
                key={tool.num} to={tool.path}
                className="tool-tile"
                style={{
                  display: 'block', textDecoration: 'none', color: T.ink,
                  padding: '28px 24px 28px 0',
                  paddingLeft: i % 2 === 1 ? 24 : 0,
                  borderRight: i % 2 === 0 ? `1px solid ${T.rule}` : 'none',
                  borderBottom: `1px solid ${T.rule}`,
                }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  marginBottom: 16,
                }}>
                  <div style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.inkFaint,
                  }}>
                    {tool.num} {tool.signature ? '/ Signature' : ''}
                  </div>
                  <div style={{
                    width: 36, height: 36,
                    border: `1px solid ${T.rule}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: T.inkSoft, fontSize: 16,
                  }}>
                    {tool.icon}
                  </div>
                </div>
                <h3 style={{
                  fontFamily: fontDisplay, fontSize: 24, fontWeight: 400,
                  lineHeight: 1.15, letterSpacing: '-0.015em',
                  margin: '0 0 10px',
                }}>
                  {tool.name.replace(tool.italic, '')}<em style={{ fontStyle: 'italic', fontWeight: 400 }}>{tool.italic}</em>
                </h3>
                <p style={{
                  fontFamily: fontBody, fontSize: 14,
                  lineHeight: 1.5, color: T.inkSoft,
                  margin: '0 0 16px',
                }}>
                  {tool.desc}
                </p>
                <div style={{
                  display: 'inline-block',
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.15em', color: T.ink,
                  border: `1px solid ${T.rule}`,
                  padding: '6px 12px',
                }}>
                  {tool.tag}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* From the blog — recent posts strip */}
        <section className="reveal reveal-4" style={{ marginBottom: 100 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            marginBottom: 16, flexWrap: 'wrap', gap: 8,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
            }}>
              § FROM THE BLOG
            </div>
            <Link to="/blog" className="ghost-link" style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.inkSoft,
              textDecoration: 'none',
            }}>
              ALL POSTS →
            </Link>
          </div>
          <h2 className="hero-h1" style={{
            fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
            lineHeight: 1.06, letterSpacing: '-0.025em',
            margin: '0 0 12px', maxWidth: 800,
          }}>
            The <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>weekly column</em>. Seasonal, contrarian, occasionally angry about TikTok.
          </h2>

          <div className="blog-strip" style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${RECENT_BLOG_POSTS.length}, 1fr)`,
            gap: 0, marginTop: 48,
            borderTop: `1px solid ${T.rule}`,
          }}>
            {RECENT_BLOG_POSTS.map((p, i) => {
              const formatDate = (iso) => {
                if (!iso) return '';
                const d = new Date(iso + 'T12:00:00');
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
              };
              const isLast = i === RECENT_BLOG_POSTS.length - 1;
              return (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="pillar-tile blog-strip-card"
                  style={{
                    display: 'flex', flexDirection: 'column',
                    textDecoration: 'none', color: T.ink,
                    padding: '28px 24px',
                    borderRight: !isLast ? `1px solid ${T.rule}` : 'none',
                    borderBottom: `1px solid ${T.rule}`,
                    minHeight: 240,
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.18em',
                    marginBottom: 16,
                  }}>
                    <span style={{ color: T.sienna }}>
                      {BLOG_CATEGORY_LABELS[p.category]?.toUpperCase() || 'POST'}
                    </span>
                    <span style={{ color: T.inkFaint }}>{formatDate(p.publishedDate)}</span>
                  </div>
                  <h3 style={{
                    fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
                    lineHeight: 1.2, letterSpacing: '-0.015em',
                    margin: '0 0 12px',
                  }}>
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p style={{
                      fontFamily: fontBody, fontSize: 13,
                      lineHeight: 1.55, color: T.inkSoft,
                      margin: '0 0 16px', flex: 1,
                    }}>
                      {truncatePlain(p.excerpt, 140)}
                    </p>
                  )}
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.inkFaint,
                    marginTop: 'auto',
                  }}>
                    {p.author?.name?.toUpperCase() || 'STAFF'} · {p.readTime}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Article index */}
        <section className="reveal reveal-4" style={{ marginBottom: 100 }}>
          <div style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            marginBottom: 16,
          }}>
            § THIS WEEK'S INDEX
          </div>
          <h2 className="hero-h1" style={{
            fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
            lineHeight: 1.06, letterSpacing: '-0.025em',
            margin: '0 0 12px', maxWidth: 800,
          }}>
            What's actually being read. <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>Live from the archive</em>, sorted by what's solving problems right now.
          </h2>

          <div style={{
            marginTop: 48,
            borderTop: `1px solid ${T.rule}`,
          }}>
            {ARTICLES.map((a) => {
              const titleParts = a.italic && a.title.includes(a.italic)
                ? a.title.split(a.italic)
                : null;
              const img = getArticleImage(a.slug);
              return (
                <Link
                  key={a.num}
                  to={a.slug.startsWith('#') ? '#' : `/articles/${a.slug}`}
                  className="article-row hp-row"
                  style={{
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none', color: T.ink,
                  }}
                >
                  <div style={{
                    width: 56, height: 56, flexShrink: 0,
                    background: T.surface, overflow: 'hidden',
                    border: `1px solid ${T.rule}`,
                  }}>
                    {img && (
                      <img
                        src={img.thumb}
                        alt={img.alt}
                        width={56}
                        height={56}
                        loading="lazy"
                        decoding="async"
                        style={{
                          width: '100%', height: '100%',
                          objectFit: 'cover', display: 'block',
                        }}
                      />
                    )}
                  </div>
                  <div className="hp-num" style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.sienna,
                  }}>
                    № {a.num}
                  </div>
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 18,
                    lineHeight: 1.4,
                  }}>
                    {titleParts ? (
                      <>{titleParts[0]}<em style={{ fontStyle: 'italic' }}>{a.italic}</em>{titleParts[1]}</>
                    ) : a.title}
                  </div>
                  <div style={{ color: T.inkFaint, textAlign: 'right' }}>→</div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ─── THE WIRE (s69) ─────────────────────────────────────────
            Industry-news section sourced from src/data/news.js. Sits
            between the article-rows and the newsletter so the main
            editorial content still leads the page. Component-only —
            no data lives here. To rotate items, edit src/data/news.js
            and add the matching blog post under src/blog/news-wire-*.md.
            See the header comment in src/data/news.js for the full
            4-step workflow. */}
        <WireSection
          T={T}
          fontDisplay={fontDisplay}
          fontBody={fontBody}
          fontMono={fontMono}
        />

        {/* Newsletter */}
        <section style={{
          padding: '56px 0',
          borderTop: `1px solid ${T.rule}`,
          borderBottom: `1px solid ${T.rule}`,
          marginBottom: 80,
        }}>
          <h2 style={{
            fontFamily: fontDisplay, fontSize: 44, fontWeight: 400,
            lineHeight: 1.1, letterSpacing: '-0.02em',
            margin: '0 0 32px',
            maxWidth: 580,
          }}>
            One field note, one tool, <em style={{ fontStyle: 'italic', color: T.sienna, fontWeight: 400 }}>one obsession</em> per week.
          </h2>

          <div style={{
            border: `1px solid ${T.rule}`,
            padding: '32px 32px 24px',
            maxWidth: 720,
          }}>
            {!subscribed ? (
              <form onSubmit={handleSubscribe}>
                <div style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.inkSoft,
                  marginBottom: 16,
                }}>
                  SUBSCRIBE TO THE DISPATCH
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${T.ink}`,
                    fontFamily: fontDisplay, fontStyle: 'italic',
                    fontSize: 22, color: T.sage,
                    padding: '8px 0', marginBottom: 24,
                    outline: 'none',
                  }}
                />
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', flexWrap: 'wrap', gap: 16,
                }}>
                  <button type="submit" style={{
                    background: 'transparent',
                    border: `1px solid ${T.ink}`,
                    color: T.ink,
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em',
                    padding: '12px 24px', cursor: 'pointer',
                  }}>
                    SUBSCRIBE →
                  </button>
                  <span style={{
                    fontFamily: fontBody, fontSize: 13,
                    color: T.inkSoft, lineHeight: 1.5,
                  }}>
                    No spam. No upsells. Unsubscribe with one click. Roughly 4,000 readers.
                  </span>
                </div>
              </form>
            ) : (
              <div style={{
                fontFamily: fontDisplay, fontSize: 18, fontStyle: 'italic',
                color: T.sienna,
              }}>
                ◆ Subscribed. Thank you.
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
